import React from "react";
import cx from "classnames";
import _isEqual from "lodash/isEqual";
import { CrossThickIcon } from "assets";

import { IPhoto } from "models/api";

import { PhotoGallery } from "components/PhotoGalery/PhotoGallery";

import "./PhotoGalleryWrapper.scss";

export const GALLERY_CONTAINER_HEIGHT = 50;

export interface IPhotoGalleryWrapperProps {
  photos: IPhoto[];
  onSelect?: (photo: IPhoto) => void;
  selectedPhotoId: string;
  moreButtonText: string;
  wrapperWidth: number;
}

export interface IPhotoGalleryWrapperState {
  isOverflow: boolean;
  isOpen: boolean;
}

export class PhotoGalleryWrapper extends React.Component<
  IPhotoGalleryWrapperProps,
  IPhotoGalleryWrapperState
> {
  private photoGalleryContainerRef = React.createRef<HTMLDivElement>();

  public readonly state: Readonly<IPhotoGalleryWrapperState> = {
    isOverflow: false,
    isOpen: false,
  };

  public componentDidMount() {
    this.adaptToWindow();
  }

  public shouldComponentUpdate(
    nextProps: Readonly<IPhotoGalleryWrapperProps>,
    nextState: Readonly<IPhotoGalleryWrapperState>
  ): boolean {
    const { wrapperWidth, selectedPhotoId, moreButtonText, photos } =
      this.props;

    return (
      this.state !== nextState ||
      nextProps.wrapperWidth !== wrapperWidth ||
      selectedPhotoId !== nextProps.selectedPhotoId ||
      moreButtonText !== nextProps.moreButtonText ||
      !_isEqual(photos, nextProps.photos)
    );
  }

  public componentDidUpdate(prevProps: Readonly<IPhotoGalleryWrapperProps>) {
    if (this.props.wrapperWidth !== prevProps.wrapperWidth) {
      this.adaptToWindow();
    }
  }

  public render() {
    const { photos, selectedPhotoId, onSelect, moreButtonText } = this.props;
    const { isOverflow, isOpen } = this.state;

    return (
      <div
        className={cx("c-photo-gallery-wrapper", {
          "c-photo-gallery-wrapper--isOverflow": isOverflow,
        })}
      >
        <div
          className={cx("c-photo-gallery-wrapper__content", {
            "c-photo-gallery-wrapper__content--isOpen": isOpen,
          })}
        >
          {isOpen && (
            <div
              className="c-photo-gallery-wrapper__close-button"
              onClick={this.hidePhotos}
              tabIndex={0}
            >
              <CrossThickIcon />
            </div>
          )}
          <div
            ref={this.photoGalleryContainerRef}
            className={cx("c-photo-gallery-wrapper__container", {
              "c-photo-gallery-wrapper__container--isOpen": isOpen,
            })}
          >
            <PhotoGallery
              photos={photos}
              selectedPhotoId={selectedPhotoId}
              onSelect={onSelect}
            />
          </div>
        </div>
        {isOverflow && !isOpen && (
          <a
            className="c-photo-gallery-wrapper__more-button g-alternative-link g-alternative-link--lg"
            onClick={this.showMorePhotos}
            tabIndex={0}
          >
            {moreButtonText}
          </a>
        )}
      </div>
    );
  }

  private showMorePhotos = () => {
    this.setState({
      isOpen: true,
    });
  };

  private hidePhotos = () => {
    this.setState({
      isOpen: false,
    });
  };

  private adaptToWindow = () => {
    const container = this.photoGalleryContainerRef.current!;

    this.setState({
      isOverflow: container.scrollHeight > GALLERY_CONTAINER_HEIGHT,
    });
  };
}
